import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { apiCall } from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

interface Role {
    id: number;
    name: string;
    permissions: Permission[];
}

interface Permission {
    id: number;
    name: string;
}

const RolesPage: React.FC = () => {
    const { token } = useAuth();
    const [roles, setRoles] = useState<Role[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

    const [formData, setFormData] = useState({
        name: "",
        permissions: [] as string[]
    });

    useEffect(() => {
        fetchRoles();
        fetchPermissions();
    }, [token]);

    const fetchRoles = async () => {
        if (!token) return;
        try {
            const data: any = await apiCall(token, '/roles');
            setRoles(data);
        } catch (error) {
            console.error("Error fetching roles:", error);
            toast.error("Erreur lors du chargement des rôles");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPermissions = async () => {
        if (!token) return;
        try {
            const data: any = await apiCall(token, '/permissions');
            setPermissions(data);
        } catch (error) {
            console.error("Error fetching permissions:", error);
        }
    };

    const handleOpenDialog = (role?: Role) => {
        if (role) {
            setEditingRole(role);
            setFormData({
                name: role.name,
                permissions: role.permissions.map(p => p.name)
            });
        } else {
            setEditingRole(null);
            setFormData({ name: "", permissions: [] });
        }
        setIsDialogOpen(true);
    };

    const handlePermissionToggle = (permName: string) => {
        setFormData(prev => {
            const current = prev.permissions;
            if (current.includes(permName)) {
                return { ...prev, permissions: current.filter(p => p !== permName) };
            } else {
                return { ...prev, permissions: [...current, permName] };
            }
        });
    };

    const handleSubmit = async () => {
        if (!token) return;
        try {
            if (editingRole) {
                await apiCall(token, `/roles/${editingRole.id}`, 'PUT', formData);
                toast.success("Rôle mis à jour");
            } else {
                await apiCall(token, '/roles', 'POST', formData);
                toast.success("Rôle créé");
            }
            setIsDialogOpen(false);
            fetchRoles();
        } catch (error) {
            console.error("Error saving role:", error);
            toast.error("Erreur lors de l'enregistrement");
        }
    };

    const handleDelete = async (id: number) => {
        if (!token) return;
        try {
            await apiCall(token, `/roles/${id}`, 'DELETE');
            toast.success("Rôle supprimé");
            fetchRoles();
        } catch (error: any) {
            console.error("Error deleting role:", error);
            toast.error("Erreur lors de la suppression");
        }
    };

    return (
        <div className="p-8 space-y-8 animate-in fade-in duration-500">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900">Gestion des Rôles</h1>
                    <p className="text-slate-500 mt-2">Définissez les rôles et leurs permissions d'accès.</p>
                </div>
                <Button onClick={() => handleOpenDialog()} className="bg-primary hover:bg-primary/90">
                    <Plus className="mr-2 h-4 w-4" /> Nouveau Rôle
                </Button>
            </div>

            <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Nom du Rôle</TableHead>
                                <TableHead>Permissions</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {roles.map((role) => (
                                <TableRow key={role.id}>
                                    <TableCell className="font-medium">{role.name}</TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            {role.permissions.map(p => (
                                                <span key={p.id} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700">
                                                    {p.name}
                                                </span>
                                            ))}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(role)}>
                                                <Pencil className="h-4 w-4 text-slate-500" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => setConfirmDeleteId(role.id)}>
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editingRole ? "Modifier le Rôle" : "Nouveau Rôle"}</DialogTitle>
                        <DialogDescription>
                            Configurez le nom du rôle et ses permissions.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Nom du rôle</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="ex: Manager"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Permissions</Label>
                            <div className="grid grid-cols-2 gap-2 border rounded-md p-4 max-h-60 overflow-y-auto">
                                {permissions.map(perm => (
                                    <div key={perm.id} className="flex items-center space-x-2">
                                        <Checkbox
                                            id={`perm-${perm.id}`}
                                            checked={formData.permissions.includes(perm.name)}
                                            onCheckedChange={() => handlePermissionToggle(perm.name)}
                                        />
                                        <Label htmlFor={`perm-${perm.id}`} className="text-sm font-normal cursor-pointer">
                                            {perm.name}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Annuler</Button>
                        <Button onClick={handleSubmit}>Enregistrer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={confirmDeleteId !== null}
                onOpenChange={(open) => { if (!open) setConfirmDeleteId(null); }}
                title="Supprimer le rôle"
                description="Êtes-vous sûr de vouloir supprimer ce rôle ?"
                confirmLabel="Supprimer"
                onConfirm={() => { if (confirmDeleteId !== null) handleDelete(confirmDeleteId); }}
            />
        </div>
    );
};

export default RolesPage;
